import catalogue from './compliance-jurisdictions.json';
const VERSION = catalogue.version;
const NI = 'Northern Ireland';
const countries = new Set(catalogue.countries);
const niById = new Map(catalogue.niRows.map(r => [r.id, r]));
const niRefsById = {};
for (const [ref, id] of Object.entries(catalogue.psniReferences)) (niRefsById[id] ||= []).push(ref);
const GB = new Set(catalogue.gbOnly);
const ENGLAND = new Set(catalogue.englandOnly);
const parentOf = { 'speakup':'wb-speakup','complaints':'complaints','temp':'env-temperature','cdcab':'sec-cd-cabinet','disposal':'tos-disposal','signpost':'tos-signposting','privacy':'gdpr-privacy','itpmr':'it-pmr-secure','cdao':'cd-officer','pom':'sec-pom-access','pmed':'sec-pmedicine','pqsaudit':'pqs-pf-audit' };
function elementId(row) { return typeof row === 'string' ? row.replace(/^el:/, '') : (row?.elementId || row?.id || row?.rowId?.replace(/^el:/, '') || ''); }

function niGuidanceValue(row, field, value) {
  const def = niById.get(elementId(row));
  if (!def) return value;
  const text = String(value || '').trim();
  const legacy = !text || /^Evidence for PSNI community-premises indicator P\d+\.\d+\. Record the actual arrangements and supporting evidence; assess against the linked indicator and current applicable NI guidance\.$/.test(text)
    || (def.legacyWording || []).some(v => String(v).trim() === text);
  return legacy ? (def[field] || (field === 'explanation' ? def.help : '') || value) : value;
}

function locationOf(branch, data) {
  if (branch?.jurisdiction) return branch.jurisdiction;
  return data?.sites?.[branch?.dbName]?.jurisdiction || null;
}
function rowApplicability(row, loc) {
  const id = elementId(row);
  if (!countries.has(loc?.nation)) return { applicable: false, unknown: true, reason: 'Location unresolved — check the pharmacy postcode.' };
  if (['ni-p13-1','ni-p13-2'].includes(id)) return { applicable: false, unknown: false, reason: 'Superseded by shared SOP management checks.' };
  let allowed = true;
  if (id.startsWith('ni-')) allowed = loc.nation === NI && niById.has(id);
  else if (id.startsWith('pqs-') || id.startsWith('dsp-nhs') || ENGLAND.has(id)) allowed = loc.nation === 'England';
  else if (id.startsWith('dsp-') || GB.has(id)) allowed = loc.regulator === 'GPHC';
  if (allowed && id.includes('--') && !id.startsWith('ni-')) {
    const parent = parentOf[id.split('--')[0]];
    if (parent) return rowApplicability(parent, loc);
  }
  return { applicable: allowed, unknown: false, reason: allowed ? '' : `Not applicable to ${loc.nation} / ${loc.regulator === 'PSNI' ? 'PSNI' : 'GPhC'}.` };
}
function detailsFor(row, loc) {
  const id = elementId(row), ni = loc?.regulator === 'PSNI';
  const refs = ni ? (niRefsById[id] || []) : [];
  const def = niById.get(id);
  const reference = ni ? (refs.length ? `PSNI ${refs.join(', ')}` : 'PSNI / applicable NI requirements') : loc?.regulator === 'GPHC' ? 'GPhC / applicable national requirements' : 'Location unresolved';
  let help = def?.help || row?.help || row?.explanation || '';
  let link = def?.link || row?.link || '';
  if (id === 'reg-pharmacist') {
    help = ni ? 'Evidence current PSNI pharmacist registration. A GPhC registration alone does not establish eligibility to practise in Northern Ireland. Uploaded copies are checked against the PSNI register/document type; ambiguous evidence goes to head office.' : 'Evidence current GPhC pharmacist registration.';
    link = ni ? 'https://registers.psni.org.uk/' : 'https://www.pharmacyregulation.org/registration';
  } else if (ni && id === 'rp-displayed') {
    help = 'Display the Responsible Pharmacist name and PSNI registration number. Evidence the notice at this pharmacy.'; link = catalogue.sources.psni;
  } else if (ni && refs.length && !def?.link) { link = catalogue.sources.psni; }
  return { reference, refs, help, link, instructions: def?.instructions || help, explanation: def?.explanation || help, satisfies: def?.satisfies || '', references: def?.references || [], regulator: loc?.regulator || null };
}
function decorateGroups(baseGroups, locations = [], { dsp = false } = {}) {
  const hasNi = locations.some(l => l?.nation === NI);
  const knownLocations = locations.filter(l => countries.has(l?.nation));
  return baseGroups.map(g => {
    const items = (g.items || []).map(it => ({ ...it, elements: (it.elements || []).map(el => ({ ...el,
      ...(el.id === 'reg-pharmacist' ? {label:'Pharmacist registration current',help:'Evidence current registration with the regulator for this pharmacy.'} : {})
    })) }));
    const sopItem = items.find(it => it.id === 'sops');
    if (sopItem) {
      sopItem.metrics = []; // Existing review/read measures now support the five checks, not extra scores.
      for (const def of catalogue.sharedSopRows || []) if (!sopItem.elements.some(el => el.id === def.id)) sopItem.elements.push({...def});
    }
    if (hasNi) for (const def of catalogue.niRows) if (def.groupId === g.id) {
      let it = items.find(x => x.id === def.itemId);
      if (!it) { it = { id: def.itemId, name: 'NI requirements', elements: [] }; items.push(it); }
      if (!it.elements.some(x => x.id === def.id)) it.elements.push({ ...def });
    }
    return { ...g, ...(g.id === 'nhs' ? { name: 'Services and contracts', subtitle: 'Nation-specific requirements' } : {}), ...(g.id === 'ig' ? { subtitle: 'Data protection and national assurance' } : {}), subtitle: g.id === 'nhs' ? 'Nation-specific requirements' : g.id === 'ig' ? 'Data protection and national assurance' : /^GPhC Principle/.test(g.subtitle || '') ? 'Shared and regulator-specific checks' : g.subtitle,
      items: items.map(it => ({ ...it, elements: it.elements.filter(el => !knownLocations.length || knownLocations.some(loc => rowApplicability(el, loc).applicable)) }))
        .filter(it => it.elements.length || (it.metrics || []).length) };
  }).filter(g => g.items.length);
}
function badge(loc) { return loc?.regulator ? `${loc.regulator === 'GPHC' ? 'GPhC' : 'PSNI'} · ${loc.nation}` : 'Location unresolved'; }

export { VERSION, catalogue, elementId, locationOf, rowApplicability, detailsFor, decorateGroups, badge, niGuidanceValue };
